import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { GithubAiIconUrl } from "./icon.ts";
import { githubAiMcpTools } from "./mcp-tools.ts";

const GITHUB_MCP_URL = "https://api.githubcopilot.com/mcp/x/all";

// The gating step is the IAM binding on the vaulted secret, not GitHub itself:
// the token is written at grant time, but the session cannot read it until the
// binding propagates.
const githubPropagationHint = propagationHint({
  resource: "GitHub",
  normalWait: "15 to 45 seconds",
  maxWait: "2 minutes",
  pollInterval: "10 seconds",
});

export const githubAiMcp: McpServerSpec & { type: "external" } = {
  type: "external",
  label: "GitHub",
  iconUrl: GithubAiIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: githubAiMcpTools },
  authorization: "upstream",
  url: GITHUB_MCP_URL,
  access: {
    prompt: `Request access to a GitHub repository by calling with argv:

  [<organization>, <repository>, <policy>..., "--reason", <reason>]

where:

  - organization: The GitHub organization owning the repository
  - repository: The repository to which access is requested, without the organization prefix
  - policy: One or more permissions, each formatted "<permission>:<level>"
    (e.g. 'contents:read', 'pull_requests:write'). Request every permission the
    tools you intend to call need, and no others. Use the list tool to enumerate them.
  - reason: The reason access is needed
`,
    prefixArgs: ["github-ai", "permission"],
    propagationHint: githubPropagationHint,
  },
  list: {
    fields: {
      organization: {
        description: "GitHub organization",
        required: [],
      },
      repository: {
        description: "GitHub repository, listed for one organization",
        required: ["organization"],
      },
      policies: {
        description:
          'GitHub repository permission, formatted "<permission>:<level>"',
        required: ["organization"],
      },
    },
    prefixArgs: ["github-ai", "permission"],
  },
};
